export interface Withdraw {
  _id: string
  walletAddress: string
  amount: string
  txId: string
  createdAt: string
  updatedAt: string
}

export interface LoginInfo {
  resource: number
  water: number
  nickName: string
  walletAddress?: string
  /** Linked Solana pubkey for email/Google accounts (`walletAddress` stays stable `eclip_vw_*`). On-chain ops use this when set. */
  realWalletAddress?: string
  wallet_type?: 'real' | 'virtual'
  premium: any
  opendPlace: any
  stakedDiamond: any
  stakedBirds: any
  miningModule: any
  miningRequest: number
  goldMine: any
  goldMineRequest: number
  uraniumMine: any
  uraniumMineRequest: number
  powerMine: any
  powerMineRequest: number
  withdrawLimit: number
  lastWithdraw: any
  userRef: string
  referrals: number
  earned: number
  discord: string
  withdraws: Withdraw[]
  wall: number
  tokenAmount: any
  ecsTokenAmount: number
  pvpMmr?: number
  /** Alliance / world-map MvM hex battles only (not PvP lobby). */
  mvmWins?: number
  mvmLosses?: number
  mvmLongestWinStreak?: number
  usdt?: number
  wheat?: number
  bread?: number
  wood?: number
  stone?: number
  iron?: number
  level: number
  userExp?: number
  userLvl?: number
  /** XP gained toward the next level (optional; from API). */
  userExpTowardNextLevel?: number
  /** XP required to reach the next level (optional; from API). */
  userExpRequiredForNextLevel?: number
  avatar?: string
  buildings: {
    bank: {
      level: number
      hp: number
      maxHp: number
    }
    wall: {
      level: number
      hp: number
      maxHp: number
    }
    well_1: {
      level: number
      hp: number
      maxHp: number
    }
    well_2: {
      level: number
      hp: number
      maxHp: number
    }
    tower: {
      level: number
      hp: number
      maxHp: number
    }
    hospital: {
      level: number
      hp: number
      maxHp: number
    }
    support: {
      level: number
      hp: number
      maxHp: number
    }
    hunting: {
      level: number
      hp: number
      maxHp: number
    }
    training: {
      level: number
      hp: number
      maxHp: number
    }
    bakery?: {
      level: number
      hp: number
      maxHp: number
    }
    bakery_2?: {
      level: number
      hp: number
      maxHp: number
    }
  }
  currentAvatar?: string | null | { id: string | null; imageUrl: string | null }
  purchasedAvatars?: string[]
  isBlocked?: boolean
  counterEspionageLevel?: number
  counterEspionageCharacterId?: string | null
  /** Account email (owner profile only; omitted for other players). */
  email?: string
  /** True when signed in with a real Solana wallet and no email on file — prompt to add email. */
  needsEmailBinding?: boolean
  /** Account creation time from MongoDB `timestamps`. */
  createdAt?: string
  /** Current energy (stamina). Regenerates over time up to maxEnergy. */
  energy?: number
  /** ISO timestamp of when `energy` was last written (used for client-side lazy regen). */
  energyUpdatedAt?: string | null
  /** Share of the player's alliance tasks completed, 0-100. Server-provided; defaults to 100. */
  allianceTaskCompletionPercent?: number
  /**
   * Timed protection shield expiry (ISO), or null when no timed shield is active.
   * Drives the world-map castle countdown and the inventory shield state.
   */
  protectionShieldExpiresAt?: string | null
  /**
   * What the active shield blocks:
   * 'item'   — purchased: world-map PvP AND mainland barbarian invasions.
   * 'defeat' — auto 8h after losing a castle defense: world-map PvP only.
   */
  protectionShieldSource?: 'item' | 'defeat' | null
  /**
   * ISO timestamp of when the once-per-account FREE mining Immediate
   * (wood/stone/iron) was consumed; null/undefined = still available.
   * Server-authoritative (`User.freeImmediateUsedAt`); mirrored here so the
   * free offer is shown at most once.
   */
  freeImmediateUsedAt?: string | null
  /**
   * ISO timestamp of when the once-per-account FREE hospital heal was consumed;
   * null/undefined = still available. Server-authoritative (`User.freeHealUsedAt`).
   */
  freeHealUsedAt?: string | null
  /**
   * ISO timestamp of when the once-per-account FREE blacksmith craft Immediate
   * was consumed; null/undefined = still available.
   */
  freeCraftImmediateUsedAt?: string | null
  /**
   * ISO timestamp of when the once-per-account FREE water (well) Immediate was
   * consumed; null/undefined = still available.
   */
  freeWaterImmediateUsedAt?: string | null
  /**
   * One-time UI explanation keys already shown to this player (FTUE phase 2 —
   * first-press / on-unlock explainers). Server-authoritative shared set.
   */
  seenExplanations?: string[]
}

export interface LoginState {
  user: LoginInfo
}

export const GET_RESOURCES_SUCCESS = 'GET_RESOURCES_SUCCESS'
export const HUNTER_UPGRADE_START_SUCCESS = 'HUNTER_UPGRADE_START_SUCCESS'
export const RESOURCE_CHANGE_SUCCESS = 'RESOURCE_CHANGE_SUCCESS'
export const BUY_LEVEL_SUCCESS = 'BUY_LEVEL_SUCCESS'
export const CHECK_COOLDOWN_SUCCESS = 'CHECK_COOLDOWN_SUCCESS'
export const LEVELUP_HUNTER_SUCCESS = 'LEVELUP_HUNTER_SUCCESS'
export const CLAIM_SIREN_SUCCESS = 'CLAIM_SIREN_SUCCESS'
export const CLAIM_HUNTER_SUCCESS = 'CLAIM_HUNTER_SUCCESS'
export const CHECK_UPGRADE_AVAILABLE = 'CHECK_UPGRADE_AVAILABLE'
export const SET_COOLDOWN_SUCCESS = 'SET_COOLDOWN_SUCCESS'

export const SWAP_RESOURCES_SUCCESS = 'SWAP_RESOURCES_SUCCESS'
export const SWAP_EGGS_SUCCESS = 'SWAP_EGGS_SUCCESS'
export const STAKE_DIAMOND_SUCCESS = 'STAKE_DIAMOND_SUCCESS'
export const STAKE_BIRD_SUCCESS = 'STAKE_BIRD_SUCCESS'
export const CLAIM_DIAMOND_SUCCESS = 'CLAIM_DIAMOND_SUCCESS'
export const CLAIM_BIRD_SUCCESS = 'CLAIM_BIRD_SUCCESS'
export const GET_WITHDRAW_AMOUNT = 'GET_WITHDRAW_AMOUNT'
export const ERROR = 'ERROR'

interface getResources {
  type: typeof GET_RESOURCES_SUCCESS
  payload: {
    data: any
  }
}

interface changeSuccess {
  type: typeof RESOURCE_CHANGE_SUCCESS
  payload: {
    data: any
  }
}

export type ActionTypes = getResources | changeSuccess
